import * as React from "react";
import { FormEvent } from "react";
import ToolTip from "react-tooltip";
import styled from "styled-components";
import LoginBG from "../../../../images/login-bg.jpg";
import logo from "../../../../images/logo.svg";
import bp from "../../../styles/breakpoints";
import colors from "../../../styles/colors";
import fonts from "../../../styles/fonts";

const Background = styled.div`
  width: 100vw;
  height: 100vh;

  background-image: url(${LoginBG});
  background-attachment: fixed;
  background-size: cover;
  background-position: center;
  -webkit-background-size: cover;
  -moz-background-size: cover;
  -o-background-size: cover;
  background-size: cover;

  display: grid;
  place-items: center;
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  font-family: ${fonts.OpenSans};
  box-shadow: 0 0.3rem 0.3rem -0.2rem rgb(0 0 0 / 20%), 0 0.3rem 0.4rem 0 rgb(0 0 0 / 14%),
    0rem 0.1rem 0.8rem 0 rgb(0 0 0 / 12%);

  @media (${bp.min800}) {
    flex-direction: row;
    width: 78rem;
  }

  @media (${bp.minH800}) {
    height: 68rem;
  }
`;

const FormContainer = styled.div`
  padding: 1rem;
  border-radius: 0 0 0.5rem 0.5rem;
  border-top: 0.2rem solid #628cba;
  background-color: #20394f;
  color: #f4fdff;
  display: flex;
  flex-direction: column;
  justify-content: center;
  @media (${bp.min800}) {
    border-top: none;
    padding: 6%;
    border-left: 0.4rem solid #628cba;
    border-radius: 0 0.5rem 0.5rem 0;
    font-size: 1.6rem;
    width: 50%;
  }
`;

const Form = styled.form`
  width: 100%;
`;

const ForgotPassword = styled.div`
  opacity: 0.7;
  text-align: center;
  font-size: 1rem;
`;

const LogoContainer = styled.div`
  background-color: #f6f5fa;
  border-radius: 0.5rem 0.5rem 0 0;
  padding: 0.5rem 0;
  display: grid;
  place-items: center;

  @media (${bp.min800}) {
    padding: 0rem 5rem;
    border-radius: 0.5rem 0 0 0.5rem;
  }
`;

const Logo = styled.img.attrs({
  src: logo,
})`
  width: 10rem;

  @media (${bp.min800}) {
    width: 20rem;
  }
`;

const Label = styled.label`
  margin-bottom: 1rem;
  font-size: 1.3rem;
`;

const Headers = styled.div`
  margin-top: 1rem;
  height: 7.5rem;
`;

const Header = styled.h4`
  font-weight: 400;
  margin: 0;
`;

const MainHeader = styled(Header)`
  text-transform: uppercase;
`;

const Input = styled.input`
  display: block;
  border-radius: 0.5rem;
  font-size: 1.8rem;
  height: 4.5rem;
  margin-bottom: 1rem;
  text-indent: 8px;
  width: 98%;

  &:focus {
    border-bottom: 0.4rem solid ${colors.buttonBlue};
    outline: none;
  }

  ::placeholder {
    color: #cccccc;
  }
`;

const UsernameInput = styled(Input).attrs({
  name: "email",
  placeholder: "you@example.com",
  type: "text",
})``;

const PasswordInput = styled(Input).attrs({
  name: "password",
  placeholder: "YourPassword123",
  type: "password",
})``;

const ButtonsContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-around;

  @media (${bp.minH480}) {
    display: block;
  }
`;

const SubmitButton = styled.button.attrs({
  type: "submit",
})`
  display: inline-block;
  width: 100%;
  font-weight: bold;
  border: none;
  border-radius: 0.5rem;
  background: #64c2ff;
  padding: 1.4rem;
  color: white;
  margin: 0.75rem 0;
  cursor: pointer;
  text-transform: uppercase;

  :hover {
    opacity: 0.8;
  }
`;

const DisabledButton = styled(SubmitButton).attrs({
  type: "button",
})`
  opacity: 0.4;

  :hover {
    opacity: 0.4;
  }
`;

const ErrorBox = styled.div`
  align-items: center;
  background-color: ${props => (props.children ? "#ed7066" : "none")};
  border-radius: 0.5rem;
  display: flex;
  height: 3.5rem;
  margin: 0.5rem 0;
  justify-content: center;
`;

const ErrorMessage = styled.p`
  color: white;
  font-weight: bold;
`;

const NAY = styled.i`
  font-weight: bold;
`;

export interface ILoginPageProps {
  isLoading: boolean;
  error: string | null;
  onSubmit: (data: { identifier: string; password: string }) => void;
}

export interface ILoginPageState {
  identifier: string;
  password: string;
  showTooltip: boolean;
}

class LoginPage extends React.Component<ILoginPageProps, ILoginPageState> {
  constructor(props: ILoginPageProps) {
    super(props);
    this.state = { identifier: "", password: "", showTooltip: false };
  }

  public toggleTooltip = (value: boolean) => () => this.setState({ showTooltip: value });

  public render() {
    const { isLoading, error } = this.props;
    const { identifier, password } = this.state;
    return (
      <Background>
        <Container>
          <LogoContainer>
            <Logo />
          </LogoContainer>
          <FormContainer onSubmit={this.onSubmit}>
            <Headers>
              <MainHeader>Welcome</MainHeader>
              <Header>Sign into your account</Header>
            </Headers>
            <Form>
              <Label>Email / Username</Label>
              <UsernameInput disabled={isLoading} value={identifier} onChange={this.onInputChange("identifier")} />
              <Label>Password</Label>
              <PasswordInput disabled={isLoading} value={password} onChange={this.onInputChange("password")} />
              <ErrorBox>{error && <ErrorMessage>{error}</ErrorMessage>}</ErrorBox>
              <ButtonsContainer>
                <SubmitButton>Login</SubmitButton>
                <DisabledButton
                  data-for="nay"
                  data-tip=""
                  onMouseEnter={this.toggleTooltip(true)}
                  onMouseLeave={this.toggleTooltip(false)}
                >
                  Login with email link
                </DisabledButton>
              </ButtonsContainer>
              <ForgotPassword
                data-for="nay"
                data-tip=""
                onMouseEnter={this.toggleTooltip(true)}
                onMouseLeave={this.toggleTooltip(false)}
              >
                <h3>Forgot password?</h3>
              </ForgotPassword>
            </Form>
          </FormContainer>
        </Container>
        <ToolTip place="bottom" id="nay" delayHide={1}>
          {this.state.showTooltip && <NAY>Not available yet.</NAY>}
        </ToolTip>
      </Background>
    );
  }

  public onSubmit = (e: FormEvent): void => {
    e.preventDefault();
    if (this.state.identifier !== "" && this.state.password !== "") {
      this.props.onSubmit({
        identifier: this.state.identifier,
        password: this.state.password,
      });
    }
  };

  public onInputChange = (fieldname: string) => (e: any): void => {
    e.preventDefault();
    this.setState({ [fieldname]: e.target.value } as ILoginPageState);
  };
}

export default LoginPage;
